import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import PostList from './components/PostList';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import TrendingNews from './components/TrendingNews';
import AdvertisePage from './components/AdvertisePage';
import PostPage from './components/PostPage';
import useSeo from './hooks/useSeo';
import './App.css';

// ─── Admin page wrapper — handles login ↔ dashboard switch ───────────────────

function AdminPage() {
  useSeo({
    title: 'Admin Portal',
    description: 'TheScandal moderation and ad management portal.',
    path: '/admin',
    noindex: true,
    keywords: ['admin dashboard', 'moderation portal', 'content moderation'],
  });

  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const navigate = useNavigate();

  function handleLogin(t) {
    setToken(t);
  }

  function handleLogout() {
    setToken(null);
    navigate('/admin');
  }

  if (!token) return <AdminLogin onLogin={handleLogin} />;
  return <AdminDashboard onLogout={handleLogout} />;
}

function HomePage({ selectedCountry, selectedPlace }) {
  useSeo({
    title: 'Community Updates & Anonymous Reports',
    description: 'Stay informed with community-driven updates, anonymous reports, and real-time incident awareness on TheScandal.',
    path: '/',
    keywords: [
      'community updates platform',
      'anonymous reports',
      'real-time incident updates',
      'local awareness feed',
      'whistleblower tips',
    ],
  });

  return <PostList selectedCountry={selectedCountry} selectedPlace={selectedPlace} />;
}

// ─── Public header ─────────────────────────────────────────────────────────────

function Header({ selectedCountry, onCountryChange, selectedPlace, onPlaceChange, countries, theme, onToggleTheme }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  function closeMobileMenu() {
    setMobileOpen(false);
  }

  return (
    <header className={`app-header${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="header-top">
        <Link to="/" className="app-logo" onClick={closeMobileMenu}>
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo.png`}
            alt="TheScandal"
            className="app-logo-image"
          />
        </Link>
        <button
          type="button"
          className="header-menu-btn"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
        >
          <span className="menu-icon" aria-hidden="true">
            <span className="menu-icon-line" />
            <span className="menu-icon-line" />
            <span className="menu-icon-line" />
          </span>
        </button>
      </div>

      <div className="header-controls">
        <div className="header-place">
          <label htmlFor="header-place-input">Place</label>
          <input
            id="header-place-input"
            className="form-input header-place-input"
            type="text"
            value={selectedPlace}
            onChange={(e) => onPlaceChange(e.target.value)}
            placeholder="e.g. Ikeja, Nairobi, Manhattan"
            maxLength={120}
          />
        </div>
        <div className="header-country">
          <label htmlFor="header-country-select">Country</label>
          <select
            id="header-country-select"
            className="form-input header-country-select"
            value={selectedCountry}
            onChange={(e) => onCountryChange(e.target.value)}
          >
            <option value="ALL">All countries</option>
            {countries.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <nav className="app-nav">
          <button type="button" className="theme-toggle" onClick={onToggleTheme}>
            {theme === 'dark' ? 'Light' : 'Dark'} mode
          </button>
          <NavLink to="/" className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`} onClick={closeMobileMenu}>Feed</NavLink>
          <NavLink to="/trending" className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`} onClick={closeMobileMenu}>Trending Updates</NavLink>
          <Link to="/advertise" className="btn-primary nav-cta-link" onClick={closeMobileMenu}>Advertise With Us</Link>
        </nav>
      </div>
    </header>
  );
}

function AppLayout({ selectedCountry, setSelectedCountry, selectedPlace, setSelectedPlace, countries, theme, setTheme }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    document.body.classList.toggle('admin-route', isAdminRoute);
    return () => {
      document.body.classList.remove('admin-route');
    };
  }, [isAdminRoute]);

  return (
    <>
      {!isAdminRoute && (
        <Header
          selectedCountry={selectedCountry}
          onCountryChange={setSelectedCountry}
          selectedPlace={selectedPlace}
          onPlaceChange={setSelectedPlace}
          countries={countries}
          theme={theme}
          onToggleTheme={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
        />
      )}

      <main className={`app-main${isAdminRoute ? ' app-main-admin' : ''}`}>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                selectedCountry={selectedCountry === 'ALL' ? '' : selectedCountry}
                selectedPlace={selectedPlace.trim()}
              />
            }
          />
          <Route
            path="/trending"
            element={
              <TrendingNews
                selectedCountry={selectedCountry === 'ALL' ? '' : selectedCountry}
                selectedPlace={selectedPlace.trim()}
              />
            }
          />
          <Route path="/post/:id" element={<PostPage />} />
          <Route path="/advertise" element={<AdvertisePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={
            <div className="empty-state">
              <h2>404</h2>
              <p>Page not found.</p>
              <Link to="/" className="btn-primary">Go Home</Link>
            </div>
          } />
        </Routes>
      </main>

      {!isAdminRoute && (
        <footer className="app-footer">
          <p>Stay informed. Share responsibly.</p>
          <div className="footer-links">
            <Link to="/privacy">Privacy</Link>
            <span>•</span>
            <Link to="/terms">Terms</Link>
          </div>
        </footer>
      )}
    </>
  );
}

// ─── App root ─────────────────────────────────────────────────────────────────

function App() {
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const [selectedPlace, setSelectedPlace] = useState('');
  const [countries, setCountries] = useState([]);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const base = process.env.REACT_APP_SITE_URL || window.location.origin;
    const scriptId = 'seo-jsonld';
    let script = document.getElementById(scriptId);
    if (script) script.remove();

    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: 'TheScandal',
          url: base,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${base}/?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          name: 'TheScandal',
          url: base,
          description: 'Community-driven platform for anonymous reports, real-world incident updates, and public awareness.',
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('https://restcountries.com/v3.1/all?fields=name')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const names = data
          .map((item) => item?.name?.common)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setCountries(names);
      })
      .catch(() => {
        if (cancelled) return;
        setCountries([
          'Argentina', 'Australia', 'Brazil', 'Canada', 'France', 'Germany', 'Ghana', 'India', 'Kenya',
          'Nigeria', 'South Africa', 'Spain', 'United Kingdom', 'United States'
        ]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="app-shell">
        <div className="app-shell-gradient app-shell-gradient-top" aria-hidden="true" />
        <div className="app-shell-gradient app-shell-gradient-mid" aria-hidden="true" />
        <div className="app-shell-gradient app-shell-gradient-bottom" aria-hidden="true" />
        <div className="app-shell-noise" aria-hidden="true" />
        <AppLayout
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          selectedPlace={selectedPlace}
          setSelectedPlace={setSelectedPlace}
          countries={countries}
          theme={theme}
          setTheme={setTheme}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
